import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, RotateCcw, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { calculateFocalAreaCrop, clampCropValues } from '@/lib/focal-area-utils';
import { Skeleton } from '@/components/ui/skeleton';

interface ImageSize {
  width: number;
  height: number;
}

interface DiffImageViewerProps {
  beforeImageUrl: string;
  afterImageUrl: string;
  beforeMarker?: { x: number; y: number } | null;
  afterMarker?: { x: number; y: number } | null;
  beforeViewport?: { panX: number; panY: number; zoom: number } | null;
  afterViewport?: { panX: number; panY: number; zoom: number } | null;
  eventKey?: string | number; // Trigger animation when this changes
  editMode?: boolean;
  onMarkerChange?: (side: 'before' | 'after', marker: { x: number; y: number } | null) => void;
  onVisibilityChange?: (side: 'before' | 'after', visible: boolean) => void;
  onViewportChange?: (side: 'before' | 'after', viewport: { panX: number; panY: number; zoom: number }) => void;
  onSave?: () => void;
  initialBeforeVisible?: boolean;
  initialAfterVisible?: boolean;
  initialZoom?: number; // Zoom level to initialize/restore for this event
  // Pending (unsaved) edits from a previous visit to this event in the same session
  pendingBeforeMarker?: { x: number; y: number } | null;
  pendingAfterMarker?: { x: number; y: number } | null;
  pendingBeforeVisible?: boolean;
  pendingAfterVisible?: boolean;
}

export function DiffImageViewer({
  beforeImageUrl,
  afterImageUrl,
  beforeMarker,
  afterMarker,
  beforeViewport,
  afterViewport,
  eventKey,
  editMode = false,
  onMarkerChange,
  onVisibilityChange,
  onViewportChange,
  onSave,
  initialBeforeVisible = true,
  initialAfterVisible = true,
  initialZoom,
  pendingBeforeMarker,
  pendingAfterMarker,
  pendingBeforeVisible,
  pendingAfterVisible,
}: DiffImageViewerProps) {
  const [zoomLevel, setZoomLevel] = useState(initialZoom ?? beforeViewport?.zoom ?? 1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [isAnimating, setIsAnimating] = useState(false);

  // Separate pan offsets for before/after
  const [beforePanOffset, setBeforePanOffset] = useState({ x: 0, y: 0 });
  const [afterPanOffset, setAfterPanOffset] = useState({ x: 0, y: 0 });

  // Panning state
  const [isPanning, setIsPanning] = useState(false);
  const [panningTarget, setPanningTarget] = useState<'before' | 'after' | null>(null);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  // Marker editing state
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [draggingMarkerTarget, setDraggingMarkerTarget] = useState<'before' | 'after' | null>(null);
  const [markerDragStart, setMarkerDragStart] = useState({ x: 0, y: 0 });
  const [editedBeforeMarker, setEditedBeforeMarker] = useState(beforeMarker);
  const [editedAfterMarker, setEditedAfterMarker] = useState(afterMarker);
  const [beforeMarkerVisible, setBeforeMarkerVisible] = useState(true);
  const [afterMarkerVisible, setAfterMarkerVisible] = useState(true);

  // Image dimensions
  const [beforeImageSize, setBeforeImageSize] = useState<ImageSize | null>(null);
  const [afterImageSize, setAfterImageSize] = useState<ImageSize | null>(null);
  const [containerSize, setContainerSize] = useState({ w: 400, h: 400 });

  const beforeContainerRef = useRef<HTMLDivElement>(null);
  const afterContainerRef = useRef<HTMLDivElement>(null);

  // CRITICAL: Use refs for immediate synchronous checks (state updates are async!)
  const isDraggingMarkerRef = useRef(false);
  const isPanningRef = useRef(false);

  // Refs to store latest transform results for toggle marker handler
  const beforeTransformRef = useRef<{ cropX: number; cropY: number } | null>(null);
  const afterTransformRef = useRef<{ cropX: number; cropY: number } | null>(null);

  // Refs to store unclamped baseCrop values (used to correct pan start offset)
  const beforeBaseCropRef = useRef<{ cropX: number; cropY: number } | null>(null);
  const afterBaseCropRef = useRef<{ cropX: number; cropY: number } | null>(null);

  // Track container size
  useEffect(() => {
    if (!beforeContainerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ w: Math.round(width), h: Math.round(height) });
    });
    ro.observe(beforeContainerRef.current);
    return () => ro.disconnect();
  }, []);

  // Load image dimensions
  useEffect(() => {
    console.log('[DiffImageViewer] Loading images:', { beforeImageUrl, afterImageUrl });
    const loadBefore = new Image();
    loadBefore.onload = () => {
      console.log('[DiffImageViewer] Before image loaded:', { width: loadBefore.naturalWidth, height: loadBefore.naturalHeight });
      setBeforeImageSize({ width: loadBefore.naturalWidth, height: loadBefore.naturalHeight });
    };
    loadBefore.onerror = (err) => {
      console.error('[DiffImageViewer] Before image failed to load:', err);
    };
    loadBefore.src = beforeImageUrl;

    const loadAfter = new Image();
    loadAfter.onload = () => {
      console.log('[DiffImageViewer] After image loaded:', { width: loadAfter.naturalWidth, height: loadAfter.naturalHeight });
      setAfterImageSize({ width: loadAfter.naturalWidth, height: loadAfter.naturalHeight });
    };
    loadAfter.onerror = (err) => {
      console.error('[DiffImageViewer] After image failed to load:', err);
    };
    loadAfter.src = afterImageUrl;
  }, [beforeImageUrl, afterImageUrl]);

  // Base scale = fit the screenshot's full width into the pane, so zoomLevel 1
  // shows the whole page width.
  //
  // This replaced a fixed 0.75. Pricing evidence is wide: at 0.75 the pane showed
  // only a slice of the page, so the value/price columns to the right of a marked
  // row fell outside it — a "price increased" diff whose price column is off-frame.
  // And before/after screenshots are NOT captured at the same width (1920/2560
  // before-images paired with 1440 after-images), so one fixed base rendered the
  // two panes at different effective zoom over different fractions of their pages
  // and the columns didn't line up even when both screenshots were fine. Fitting
  // each pane to its own image width normalises capture width and DPR.
  const baseScaleFor = useCallback(
    (imageSize: { width: number; height: number } | null) =>
      imageSize && imageSize.width > 0 && containerSize.w > 0 ? containerSize.w / imageSize.width : 0.75,
    [containerSize]
  );

  // Calculate min zoom. zoomLevel 1 == full page width, so only the height needs
  // room to zoom out further; cap at 1 so we never start below fit-width.
  useEffect(() => {
    if (beforeImageSize && containerSize.w > 0 && containerSize.h > 0) {
      const fitZoomY = containerSize.h / (beforeImageSize.height * baseScaleFor(beforeImageSize));
      setMinZoom(Math.max(0.05, Math.min(1, fitZoomY)));
    }
  }, [beforeImageSize, containerSize, baseScaleFor]);

  // Sync edited markers with props; restore pending (unsaved) edits when switching events
  useEffect(() => {
    setEditedBeforeMarker(pendingBeforeMarker !== undefined ? pendingBeforeMarker : beforeMarker);
    setEditedAfterMarker(pendingAfterMarker !== undefined ? pendingAfterMarker : afterMarker);
    setBeforeMarkerVisible(pendingBeforeVisible ?? initialBeforeVisible);
    setAfterMarkerVisible(pendingAfterVisible ?? initialAfterVisible);
  }, [beforeMarker, afterMarker, eventKey, initialBeforeVisible, initialAfterVisible, pendingBeforeMarker, pendingAfterMarker, pendingBeforeVisible, pendingAfterVisible]);

  // Trigger animation when event changes
  useEffect(() => {
    if (eventKey === undefined) return;
    setIsAnimating(true);
    setBeforePanOffset({ x: 0, y: 0 });
    setAfterPanOffset({ x: 0, y: 0 });
    // Don't reset zoom - let it persist across events for smoother transitions
    const timer = setTimeout(() => setIsAnimating(false), 500);
    return () => clearTimeout(timer);
  }, [eventKey]);

  // Update zoom when initialZoom prop changes (event change with per-event zoom)
  useEffect(() => {
    const newZoom = initialZoom ?? beforeViewport?.zoom ?? 1;
    setZoomLevel(newZoom);
  }, [initialZoom, eventKey]);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel * 1.25, 5);
    setZoomLevel(newZoom);
    if (onViewportChange) {
      onViewportChange('before', { zoom: newZoom, panX: beforePanOffset.x, panY: beforePanOffset.y });
    }
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel / 1.25, minZoom);
    setZoomLevel(newZoom);
    if (onViewportChange) {
      onViewportChange('before', { zoom: newZoom, panX: beforePanOffset.x, panY: beforePanOffset.y });
    }
  };

  const handleZoomReset = () => {
    const resetZoom = beforeViewport?.zoom ?? 1; // Reset to SAVED zoom, not 1.0
    setZoomLevel(resetZoom);
    setBeforePanOffset({ x: 0, y: 0 });
    setAfterPanOffset({ x: 0, y: 0 });
    if (onViewportChange) {
      onViewportChange('before', { zoom: resetZoom, panX: 0, panY: 0 });
    }
  };

  // Pan handlers
  const handlePanStart = (e: React.MouseEvent, target: 'before' | 'after') => {
    // CRITICAL: Check ref immediately (state is async!)
    if (isDraggingMarkerRef.current) {
      return;
    }
    if (editMode && (e.target as HTMLElement).closest('[data-marker]')) {
      return;
    }

    e.preventDefault();

    // CRITICAL: Update refs immediately
    isDraggingMarkerRef.current = false;
    isPanningRef.current = true;

    // Also update state
    setIsDraggingMarker(false);
    setDraggingMarkerTarget(null);
    setIsPanning(true);
    setPanningTarget(target);

    const currentOffset = target === 'before' ? beforePanOffset : afterPanOffset;
    const transformRef = target === 'before' ? beforeTransformRef : afterTransformRef;
    const baseCropRef = target === 'before' ? beforeBaseCropRef : afterBaseCropRef;

    // Derive effective starting offset from the actual rendered (clamped) crop position.
    // When baseCrop.cropX > maxScrollX (marker far right), panOffset=0 is below the valid
    // minimum, creating a dead zone where dragging does nothing. By computing
    // effectiveOffset = baseCrop - actualCrop, we start panning from the real visible position.
    let offsetX = currentOffset.x;
    let offsetY = currentOffset.y;
    if (transformRef.current !== null && baseCropRef.current !== null) {
      offsetX = baseCropRef.current.cropX - transformRef.current.cropX;
      offsetY = baseCropRef.current.cropY - transformRef.current.cropY;
    }

    setPanStart({ x: e.clientX, y: e.clientY, offsetX, offsetY });
  };

  const handlePanMove = useCallback((e: MouseEvent) => {
    // CRITICAL: Check ref first (immediate check!)
    if (isDraggingMarkerRef.current) {
      return;
    }
    if (!isPanning || !panningTarget) return;

    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;

    const imageSize = panningTarget === 'before' ? beforeImageSize : afterImageSize;
    const viewport = panningTarget === 'before' ? beforeViewport : afterViewport;
    const marker = panningTarget === 'before' ? beforeMarker : afterMarker;

    if (!imageSize) {
      const newOffset = { x: panStart.offsetX + dx, y: panStart.offsetY + dy };
      if (panningTarget === 'before') setBeforePanOffset(newOffset);
      else setAfterPanOffset(newOffset);
      return;
    }

    const scale = baseScaleFor(imageSize) * zoomLevel;
    const scaledWidth = imageSize.width * scale;
    const scaledHeight = imageSize.height * scale;

    // Get fallback from other image
    const otherMarker = panningTarget === 'before' ? afterMarker : beforeMarker;
    const otherViewport = panningTarget === 'before' ? afterViewport : beforeViewport;
    let fallbackFocalCenter: { x: number; y: number } | null = null;
    const hasOtherViewport = otherViewport && typeof otherViewport.panX === 'number';
    const hasOtherMarker = otherMarker && (otherMarker.x > 0 || otherMarker.y > 0);
    if (hasOtherViewport && hasOtherMarker) {
      fallbackFocalCenter = { x: otherMarker.x, y: otherMarker.y };
    } else if (hasOtherMarker) {
      fallbackFocalCenter = { x: otherMarker.x, y: otherMarker.y };
    }

    const baseCrop = calculateFocalAreaCrop({
      viewport,
      marker,
      containerWidth: containerSize.w,
      containerHeight: containerSize.h,
      scale,
      fallbackFocalCenter
    });

    const maxScrollX = Math.max(0, scaledWidth - containerSize.w);
    const maxScrollY = Math.max(0, scaledHeight - containerSize.h);
    const minPanX = baseCrop.cropX - maxScrollX;
    const maxPanX = baseCrop.cropX;
    const minPanY = baseCrop.cropY - maxScrollY;
    const maxPanY = baseCrop.cropY;

    const newOffset = {
      x: Math.max(minPanX, Math.min(maxPanX, panStart.offsetX + dx)),
      y: Math.max(minPanY, Math.min(maxPanY, panStart.offsetY + dy))
    };

    if (panningTarget === 'before') setBeforePanOffset(newOffset);
    else setAfterPanOffset(newOffset);
  }, [isPanning, panningTarget, panStart, beforeImageSize, afterImageSize, beforeViewport, afterViewport, beforeMarker, afterMarker, zoomLevel, containerSize]);

  const handlePanEnd = useCallback(() => {
    isPanningRef.current = false;
    setIsPanning(false);

    // Notify parent of viewport change after panning
    if (onViewportChange && panningTarget) {
      const imageSize = panningTarget === 'before' ? beforeImageSize : afterImageSize;
      const viewport = panningTarget === 'before' ? beforeViewport : afterViewport;
      const marker = panningTarget === 'before' ? beforeMarker : afterMarker;
      const panOffset = panningTarget === 'before' ? beforePanOffset : afterPanOffset;

      const scale = baseScaleFor(imageSize) * zoomLevel;

      // Build fallback focal center (same logic as handlePanMove)
      const otherMarker = panningTarget === 'before' ? afterMarker : beforeMarker;
      const otherViewport = panningTarget === 'before' ? afterViewport : beforeViewport;
      let fallbackFocalCenter: { x: number; y: number } | null = null;
      const hasOtherViewport = otherViewport && typeof otherViewport.panX === 'number';
      const hasOtherMarker = otherMarker && (otherMarker.x > 0 || otherMarker.y > 0);
      if (hasOtherViewport && hasOtherMarker) {
        fallbackFocalCenter = { x: otherMarker.x, y: otherMarker.y };
      } else if (hasOtherMarker) {
        fallbackFocalCenter = { x: otherMarker.x, y: otherMarker.y };
      }

      // Compute the actual rendered crop position
      const baseCrop = calculateFocalAreaCrop({
        viewport,
        marker,
        containerWidth: containerSize.w,
        containerHeight: containerSize.h,
        scale,
        fallbackFocalCenter,
      });

      let finalCropX = baseCrop.cropX - panOffset.x;
      let finalCropY = baseCrop.cropY - panOffset.y;

      // Clamp (same as getImageTransform)
      if (imageSize) {
        const clamped = clampCropValues(
          finalCropX, finalCropY,
          imageSize.width * scale, imageSize.height * scale,
          containerSize.w, containerSize.h
        );
        finalCropX = clamped.cropX;
        finalCropY = clamped.cropY;
      }

      // Convert to image-space focal center so loading uses the same centering formula
      const focalCenterX = scale > 0 ? (finalCropX + containerSize.w / 2) / scale : 0;
      const focalCenterY = scale > 0 ? (finalCropY + containerSize.h / 2) / scale : 0;

      onViewportChange(panningTarget, {
        zoom: zoomLevel,
        panX: focalCenterX,
        panY: focalCenterY,
      });
    }

    setPanningTarget(null);
  }, [onViewportChange, panningTarget, beforePanOffset, afterPanOffset, zoomLevel,
      beforeImageSize, afterImageSize, beforeViewport, afterViewport,
      beforeMarker, afterMarker, containerSize]);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mousemove', handlePanMove);
      window.addEventListener('mouseup', handlePanEnd);
      return () => {
        window.removeEventListener('mousemove', handlePanMove);
        window.removeEventListener('mouseup', handlePanEnd);
      };
    }
  }, [isPanning, handlePanMove, handlePanEnd]);

  // Marker dragging handlers
  const handleMarkerDragStart = (e: React.MouseEvent, target: 'before' | 'after') => {
    if (!editMode) return;
    e.stopPropagation();
    e.preventDefault();

    // CRITICAL: Update refs immediately (before any state updates!)
    isPanningRef.current = false;
    isDraggingMarkerRef.current = true;

    // Also update state
    setIsPanning(false);
    setPanningTarget(null);
    setIsDraggingMarker(true);
    setDraggingMarkerTarget(target);
    setMarkerDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMarkerDragMove = useCallback((e: MouseEvent) => {
    // CRITICAL: Check ref first (immediate check!)
    if (isPanningRef.current) {
      return;
    }
    if (!isDraggingMarker || !draggingMarkerTarget) return;

    const imageSize = draggingMarkerTarget === 'before' ? beforeImageSize : afterImageSize;
    const currentMarker = draggingMarkerTarget === 'before' ? editedBeforeMarker : editedAfterMarker;

    if (!imageSize || !currentMarker) return;

    const scale = baseScaleFor(imageSize) * zoomLevel;

    // Calculate mouse movement in container coordinates
    const dx = e.clientX - markerDragStart.x;
    const dy = e.clientY - markerDragStart.y;

    // Convert container movement to image coordinates
    // When we move the marker right by dx pixels in the container,
    // we need to increase the marker's x coordinate in the image by dx/scale
    const newMarkerX = Math.max(0, Math.min(imageSize.width, currentMarker.x + dx / scale));
    const newMarkerY = Math.max(0, Math.min(imageSize.height, currentMarker.y + dy / scale));

    const newMarker = { x: Math.round(newMarkerX), y: Math.round(newMarkerY) };

    if (draggingMarkerTarget === 'before') {
      setEditedBeforeMarker(newMarker);
    } else {
      setEditedAfterMarker(newMarker);
    }

    setMarkerDragStart({ x: e.clientX, y: e.clientY });
  }, [isDraggingMarker, draggingMarkerTarget, beforeImageSize, afterImageSize, beforeViewport, afterViewport, editedBeforeMarker, editedAfterMarker, zoomLevel, markerDragStart]);

  const handleMarkerDragEnd = useCallback(() => {
    if (isDraggingMarker && draggingMarkerTarget && onMarkerChange) {
      const finalMarker = draggingMarkerTarget === 'before' ? editedBeforeMarker : editedAfterMarker;
      if (finalMarker) {
        onMarkerChange(draggingMarkerTarget, finalMarker);
      }
    }
    isDraggingMarkerRef.current = false;
    setIsDraggingMarker(false);
    setDraggingMarkerTarget(null);
  }, [isDraggingMarker, draggingMarkerTarget, editedBeforeMarker, editedAfterMarker, onMarkerChange]);

  useEffect(() => {
    if (isDraggingMarker) {
      window.addEventListener('mousemove', handleMarkerDragMove);
      window.addEventListener('mouseup', handleMarkerDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleMarkerDragMove);
        window.removeEventListener('mouseup', handleMarkerDragEnd);
      };
    }
  }, [isDraggingMarker, handleMarkerDragMove, handleMarkerDragEnd]);

  const getImageTransform = (
    imageSize: ImageSize | null,
    marker: { x: number; y: number } | null | undefined,
    viewport: { panX: number; panY: number; zoom: number } | null | undefined,
    panOffset: { x: number; y: number },
    otherMarker: { x: number; y: number } | null | undefined,
    otherViewport: { panX: number; panY: number; zoom: number } | null | undefined,
    displayMarker?: { x: number; y: number } | null | undefined  // For showing marker at edited position
  ) => {
    if (!imageSize || containerSize.w <= 0 || containerSize.h <= 0) return null;

    const scale = baseScaleFor(imageSize) * zoomLevel;
    const scaledWidth = imageSize.width * scale;
    const scaledHeight = imageSize.height * scale;

    // Get fallback
    let fallbackFocalCenter: { x: number; y: number } | null = null;
    const hasOtherViewport = otherViewport && typeof otherViewport.panX === 'number';
    const hasOtherMarker = otherMarker && (otherMarker.x > 0 || otherMarker.y > 0);
    if (hasOtherViewport && hasOtherMarker) {
      fallbackFocalCenter = { x: otherMarker.x, y: otherMarker.y };
    } else if (hasOtherMarker) {
      fallbackFocalCenter = { x: otherMarker.x, y: otherMarker.y };
    }

    const baseCrop = calculateFocalAreaCrop({
      viewport,
      marker,
      containerWidth: containerSize.w,
      containerHeight: containerSize.h,
      scale,
      fallbackFocalCenter
    });

    let cropX = baseCrop.cropX - panOffset.x;
    let cropY = baseCrop.cropY - panOffset.y;

    const clamped = clampCropValues(cropX, cropY, scaledWidth, scaledHeight, containerSize.w, containerSize.h);
    cropX = clamped.cropX;
    cropY = clamped.cropY;

    // Use displayMarker for rendering if provided (for edit mode), otherwise use marker
    const markerToDisplay = displayMarker || marker;
    const visibleX = markerToDisplay ? markerToDisplay.x * scale - cropX : 0;
    const visibleY = markerToDisplay ? markerToDisplay.y * scale - cropY : 0;
    const markerX = Math.max(8, Math.min(containerSize.w - 40, visibleX - 12));
    const markerY = Math.max(8, Math.min(containerSize.h - 32, visibleY - 12));
    const markerVisible = markerToDisplay && visibleX >= 0 && visibleX <= containerSize.w && visibleY >= 0 && visibleY <= containerSize.h;

    return { scaledWidth, scaledHeight, cropX, cropY, markerX, markerY, markerVisible, baseCropX: baseCrop.cropX, baseCropY: baseCrop.cropY };
  };

  // Toggle marker: remove if exists, add at viewport center if not
  const handleToggleMarker = (side: 'before' | 'after') => {
    const currentMarker = side === 'before' ? editedBeforeMarker : editedAfterMarker;
    if (currentMarker) {
      // Remove marker
      if (side === 'before') setEditedBeforeMarker(null);
      else setEditedAfterMarker(null);
      if (onMarkerChange) onMarkerChange(side, null);
    } else {
      // Add marker at viewport center
      const transform = side === 'before' ? beforeTransformRef.current : afterTransformRef.current;
      const imageSize = side === 'before' ? beforeImageSize : afterImageSize;
      if (transform && imageSize) {
        const scale = baseScaleFor(imageSize) * zoomLevel;
        const centerX = Math.round((transform.cropX + containerSize.w / 2) / scale);
        const centerY = Math.round((transform.cropY + containerSize.h / 2) / scale);
        const newMarker = {
          x: Math.max(0, Math.min(imageSize.width, centerX)),
          y: Math.max(0, Math.min(imageSize.height, centerY)),
        };
        if (side === 'before') setEditedBeforeMarker(newMarker);
        else setEditedAfterMarker(newMarker);
        if (onMarkerChange) onMarkerChange(side, newMarker);
      }
    }
  };

  // CRITICAL: In edit mode, use ORIGINAL marker for viewport calculation (so it doesn't recenter)
  // but use EDITED marker for display (so we see it move)
  const beforeTransform = getImageTransform(
    beforeImageSize,
    beforeMarker,  // Use original for viewport calculation
    beforeViewport,
    beforePanOffset,
    afterMarker,
    afterViewport,
    editMode ? editedBeforeMarker : undefined  // Use edited for display
  );
  const afterTransform = getImageTransform(
    afterImageSize,
    afterMarker,  // Use original for viewport calculation
    afterViewport,
    afterPanOffset,
    beforeMarker,
    beforeViewport,
    editMode ? editedAfterMarker : undefined  // Use edited for display
  );

  // Keep refs in sync for toggle marker handler and pan start correction
  if (beforeTransform) {
    beforeTransformRef.current = { cropX: beforeTransform.cropX, cropY: beforeTransform.cropY };
    beforeBaseCropRef.current = { cropX: beforeTransform.baseCropX, cropY: beforeTransform.baseCropY };
  }
  if (afterTransform) {
    afterTransformRef.current = { cropX: afterTransform.cropX, cropY: afterTransform.cropY };
    afterBaseCropRef.current = { cropX: afterTransform.baseCropX, cropY: afterTransform.baseCropY };
  }

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center justify-end mb-3">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleZoomOut}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-12 text-center tabular-nums">
            {Math.round(zoomLevel * 100)}%
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleZoomIn}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleZoomReset}>
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Images side by side */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Before Image */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            Before
            {editMode && (
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-5 w-5 ml-auto", editedBeforeMarker && "text-primary")}
                onClick={() => handleToggleMarker('before')}
                title={editedBeforeMarker ? "Remove marker" : "Add marker"}
              >
                <MapPin className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div
            ref={beforeContainerRef}
            className="flex-1 relative rounded overflow-hidden border border-border bg-muted/20 cursor-grab active:cursor-grabbing"
            onMouseDown={(e) => handlePanStart(e, 'before')}
          >
            {!beforeImageSize || !beforeTransform ? (
              <Skeleton className="w-full h-full" />
            ) : (
              <>
                <div
                  className="absolute select-none transition-transform duration-500 ease-out"
                  style={{
                    width: beforeTransform.scaledWidth,
                    height: beforeTransform.scaledHeight,
                    transform: `translate(${-beforeTransform.cropX}px, ${-beforeTransform.cropY}px)`
                  }}
                >
                  <img src={beforeImageUrl} alt="Before" className="w-full h-full" draggable={false} />
                </div>
                {beforeTransform.markerVisible && beforeMarkerVisible && editedBeforeMarker && (editedBeforeMarker.x > 0 || editedBeforeMarker.y > 0) && (
                  <div
                    data-marker="before"
                    className={cn(
                      "absolute flex items-center justify-center z-10 transition-all duration-500 ease-out",
                      editMode && "cursor-move hover:scale-110"
                    )}
                    style={{ left: beforeTransform.markerX, top: beforeTransform.markerY }}
                    onMouseDown={(e) => editMode && handleMarkerDragStart(e, 'before')}
                  >
                    <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded shadow-md text-black" style={{ backgroundColor: '#d8e364' }}>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* After Image */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-400" />
            After
            {editMode && (
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-5 w-5 ml-auto", editedAfterMarker && "text-primary")}
                onClick={() => handleToggleMarker('after')}
                title={editedAfterMarker ? "Remove marker" : "Add marker"}
              >
                <MapPin className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div
            ref={afterContainerRef}
            className="flex-1 relative rounded overflow-hidden border border-border bg-muted/20 cursor-grab active:cursor-grabbing"
            onMouseDown={(e) => handlePanStart(e, 'after')}
          >
            {!afterImageSize || !afterTransform ? (
              <Skeleton className="w-full h-full" />
            ) : (
              <>
                <div
                  className="absolute select-none transition-transform duration-500 ease-out"
                  style={{
                    width: afterTransform.scaledWidth,
                    height: afterTransform.scaledHeight,
                    transform: `translate(${-afterTransform.cropX}px, ${-afterTransform.cropY}px)`
                  }}
                >
                  <img src={afterImageUrl} alt="After" className="w-full h-full" draggable={false} />
                </div>
                {afterTransform.markerVisible && afterMarkerVisible && editedAfterMarker && (editedAfterMarker.x > 0 || editedAfterMarker.y > 0) && (
                  <div
                    data-marker="after"
                    className={cn(
                      "absolute flex items-center justify-center z-10 transition-all duration-500 ease-out",
                      editMode && "cursor-move hover:scale-110"
                    )}
                    style={{ left: afterTransform.markerX, top: afterTransform.markerY }}
                    onMouseDown={(e) => editMode && handleMarkerDragStart(e, 'after')}
                  >
                    <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded shadow-md text-black" style={{ backgroundColor: '#d8e364' }}>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
